import React, { FC, Fragment, ReactNode } from 'react';

import { Box, styled } from '@mui/material';
import { Link } from 'react-router-dom';

import Card from '~/components/Card/Card';
import CardDescription from '~/components/Card/components/CardDescription/CardDescription';
import CardTitle from '~/components/Card/components/CardHeader/CardTitle';
import CardInfo from '~/components/Card/components/CardInfo/CardInfo';
import CardLabels from '~/components/Card/components/CardLabels/CardLabels';
import Ratio from '~/components/Ratio';
import Skill from '~/components/Skill/Skill';
import { LightTooltip } from '~/components/UI/LightTooltip/LightTooltip';
import ZoomedBackground from '~/components/ZoomedBackground';
import { ComingSoonOverlay } from '~/pages/DiscoverPage/components/ComingSoonOverlay/ComingSoonOverlay';
import { RefinedSkill } from '~/pages/DiscoverPage/components/Hit/Hit';
import { PUBLISH_STATUSES } from '~/pages/const';
import { buildSkillRoute, FALLBACK_LINK } from '~/routes';
import { css, CssUtilityType } from '~/styled';
import { SMALL_MARGIN_PX, SMALL_RADIUS_PX, TINY_MARGIN_PX } from '~/theme';

interface IGeneralizedHit {
  className?: string;
  titleLayout: ReactNode;
  previewLayout: ReactNode;
  cpdCreditsLayout: ReactNode;
  accompanyingInfoLayout: ReactNode;
  link?: string;
  previewImg: string;
  title: string;
  description: string;
  onClick: () => void;
  categories?: ReactNode[];
  cardImgWrapperCss?: CssUtilityType;
  publish_status?: PUBLISH_STATUSES | undefined;
  skills?: RefinedSkill[];
}

const GeneralizedHit: FC<IGeneralizedHit> = ({
  className,
  cardImgWrapperCss,
  titleLayout,
  previewLayout,
  cpdCreditsLayout,
  title,
  description,
  link,
  onClick,
  accompanyingInfoLayout,
  previewImg,
  categories,
  publish_status,
  skills,
}) => {
  const comingSoon = publish_status === PUBLISH_STATUSES.ComingSoon;
  const skillsExist = skills && skills.length > 0;
  const firstSkill = skills?.at(0);
  const restSkills = skills?.slice(1);

  return (
    <Card className={className}>
      <StyledRatio ratio={68}>
        <StyledLink to={link || FALLBACK_LINK} onClick={onClick} comingSoon={comingSoon}>
          <StyledZoomedBackground path={previewImg} optionalStyles={cardImgWrapperCss}>
            {previewLayout}
            {comingSoon && <ComingSoonOverlay />}
          </StyledZoomedBackground>
        </StyledLink>
      </StyledRatio>
      <CardInfo>
        <LabelsWrapper>
          {categories && categories.length > 0 && (
            <CardLabels wrapperCss={cardLabelsWrapperCss}>{categories}</CardLabels>
          )}
          {cpdCreditsLayout}
        </LabelsWrapper>
        {title && !titleLayout && (
          <TitleWrapper
            data-testid="resultItemTitle"
            to={link || FALLBACK_LINK}
            onClick={onClick}
            comingSoon={comingSoon}
          >
            <CardTitle text={title} />
          </TitleWrapper>
        )}
        {titleLayout}
        {description && <StyledCardDescription text={description} skillsExist={skillsExist} />}
        {skillsExist && (
          <SkillBox>
            <Skill link={buildSkillRoute(firstSkill?.slug)}>{firstSkill?.title}</Skill>
            {skills.length > 1 && (
              <LightTooltip
                title={
                  <div>
                    {skills.slice(1).map((skill, i) => {
                      return (
                        <Fragment key={skill.id}>
                          <StyledSkillTooltipLink to={buildSkillRoute(skill.slug)}>
                            {skill.title}
                          </StyledSkillTooltipLink>
                          {i !== Number(restSkills?.length) - 1 && ', '}
                        </Fragment>
                      );
                    })}
                  </div>
                }
                placement="top"
              >
                <Skill as={Box}>+{restSkills?.length}</Skill>
              </LightTooltip>
            )}
          </SkillBox>
        )}
        <InfoRow>{accompanyingInfoLayout}</InfoRow>
      </CardInfo>
    </Card>
  );
};

export default GeneralizedHit;

const cardLabelsWrapperCss = css`
  &:not(:only-child) {
    margin-right: 10px;
  }
`;
const StyledRatio = styled(Ratio)`
  flex: 1;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
    max-width: 400px;
  }
`;
const StyledLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'comingSoon' })<{
  comingSoon: boolean;
}>`
  display: block;
  height: 100%;
  color: ${({ theme }) => theme.palette.common.gray};
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};
`;
const LabelsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;

  & .cpdLabel {
    padding: 4.5px 9px;
  }
`;
const TitleWrapper = styled(Link, { shouldForwardProp: (prop) => prop !== 'comingSoon' })<{
  comingSoon: boolean;
}>`
  color: ${({ theme }) => theme.palette.common.white};
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};
`;
const InfoRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const StyledZoomedBackground = styled(ZoomedBackground, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  border-radius: ${SMALL_RADIUS_PX};
  overflow: hidden;

  ${({ optionalStyles }) => optionalStyles}
`;
const SkillBox = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${TINY_MARGIN_PX};
  margin-bottom: ${TINY_MARGIN_PX};
`;
const StyledSkillTooltipLink = styled(Link)`
  color: ${({ theme }) => theme.palette.common.tooltip.link};

  &:hover {
    text-decoration: underline;
  }
`;
const StyledCardDescription = styled(CardDescription, {
  shouldForwardProp: (prop) => prop !== 'skillsExist',
})<{ skillsExist?: boolean }>`
  margin-bottom: ${({ skillsExist }) => skillsExist && TINY_MARGIN_PX};
`;
